import {Component, EventEmitter, forwardRef, Inject, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {ControlValueAccessor, FormBuilder, FormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {ReplaySubject} from 'rxjs';
import {ListResponse} from '../../interface/response/list-response';
import {DOCUMENT} from '@angular/common';
import {debounceTime, distinctUntilChanged, startWith, take, takeUntil} from 'rxjs/operators';
import {MatSelectChange} from '@angular/material/select';
import {AdditionalField} from "../../interface/additionalField";
import {AdditionalFieldsService} from "../../services/additional-fields.service";

@Component({
  selector: 'app-dict-additional-fields',
  templateUrl: './dict-additional-fields.component.html',
  styleUrls: ['./dict-additional-fields.component.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, multi: true, useExisting: forwardRef(() => DictAdditionalFieldsComponent)}
  ]
})
export class DictAdditionalFieldsComponent implements OnInit, OnDestroy, ControlValueAccessor {

  @Input() label: string;
  @Input() control: FormControl = null;
  @Input() required = false;
  @Input() excludeId?: string;
  @Input() showFieldInfo = false;
  @Input() filterType?: string;
  @Input() filterEntity?: string;

  @Output() selectedAdditionalField: EventEmitter<AdditionalField> = new EventEmitter<AdditionalField>();

  externalControl = true;
  disabled = false;
  items: AdditionalField[] = [];
  value?: string = null;
  searchFilterCtrl: FormControl;
  itemsSubject: ReplaySubject<ListResponse<AdditionalField>> = new ReplaySubject<ListResponse<AdditionalField>>(1);

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private additionalFieldsService: AdditionalFieldsService,
    @Inject(DOCUMENT) private document: Document,
    private fb: FormBuilder,
  ) {
    this.searchFilterCtrl = fb.control('');
  }
  onChange: any = () => { };
  onTouched: any = () => { };

  ngOnInit(): void {
    if (this.control === null) {
      this.control = new FormControl('');
      this.externalControl = false;
    }

    this.control.valueChanges
      .pipe(
        startWith(this.control.value),
        takeUntil(this.destroyed$)
      )
      .subscribe((val: string) => {
        if (val && typeof val === 'string') {
          const exists = this.items.some(field => field.uuid === val);

          if (!exists) {
            this.additionalFieldsService.getAdditionalField(val)
              .pipe(take(1))
              .subscribe({
                next: (field: AdditionalField) => {
                  if (field) {
                    this.items = [field, ...this.items];
                  }
                },
                error: () => {
                  console.warn('Nie znaleziono pola dodatkowego', val);
                }
              });
          }
        }
      });

    this.searchFilterCtrl.valueChanges
      .pipe(
        startWith(''),
        debounceTime(200),
        distinctUntilChanged(),
        takeUntil(this.destroyed$),
      )
      .subscribe((searchValue: string) => {
        const filters: any = {
          active: true
        };
        if (this.filterType) {
          filters.type = this.filterType;
        }

        if (this.filterEntity) {
          filters.entity = this.filterEntity;
        }

        if (this.excludeId) {
          filters.excludeId = this.excludeId;
        }

        if (searchValue) {
          filters.fieldName = searchValue;
          this.additionalFieldsService.loadNotCountedList(0, 20, filters, this.itemsSubject);
        } else {
          this.loadInitialData();
        }
      }
    );

    this.itemsSubject
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result: ListResponse<AdditionalField>) => {
        const currentVal = this.control.value;
        const selectedItem = this.items.find(i => i.uuid === currentVal);

        this.items = result.items;

        if (currentVal && selectedItem && !this.items.some(i => i.uuid === currentVal)) {
          this.items = [selectedItem, ...this.items];
        }
      });

    this.loadInitialData();
  }

  loadInitialData(): void {
    const filters: any = {
      active: true
    };

    if (this.filterType) {
      filters.type = this.filterType;
    }

    if (this.filterEntity) {
      filters.entity = this.filterEntity;
    }

    if (this.excludeId) {
      filters.excludeId = this.excludeId;
    }

    this.additionalFieldsService.loadNotCountedList(0, 10, filters, this.itemsSubject);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.externalControl) {
      return;
    }
    if (isDisabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }
    this.disabled = isDisabled;
  }

  writeValue(value: any): void {
    if (this.externalControl) {
      return;
    }
    this.control.setValue(value);
    this.value = value;
  }

  onSelectionChange(e: MatSelectChange): void {
    const selectedValue = e.value;
    this.value = selectedValue;

    if (selectedValue) {
      const selectedField = this.items.find(field => field.uuid === selectedValue);
      if (selectedField) {
        this.selectedAdditionalField.emit(selectedField);
      }
    }

    this.onChange(this.value);
  }

  clearValue(): void {
    this.control.setValue(null);
    this.writeValue(null);
    this.onChange(null);
  }
}
